import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  MCPConnection,
  useMCPConnectInfo,
  useMyMCPConnections,
  useRevokeMyMCPConnection,
} from '../api/mcp';

interface Props {
  open: boolean;
  onClose: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'never used';
  return `last used ${new Date(iso).toLocaleString()}`;
}

function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      InputProps={{
        readOnly: true,
        sx: { fontFamily: 'monospace', fontSize: 13 },
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton size="small" onClick={copy}>
                {copied ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
}

/**
 * The current user's connected AI assistants (Claude etc.): how to connect
 * one, plus a list of existing connections with a disconnect button.
 */
export default function MyAIConnectionsDialog({ open, onClose }: Props) {
  const { data: info } = useMCPConnectInfo();
  const { data: connections = [], isLoading } = useMyMCPConnections();
  const revoke = useRevokeMyMCPConnection();
  const [revokingId, setRevokingId] = useState<number | null>(null);

  const handleRevoke = (conn: MCPConnection) => {
    setRevokingId(conn.id);
    revoke.mutate(conn.id, { onSettled: () => setRevokingId(null) });
  };

  const mcpUrl = info?.mcp_url ?? '';
  const disabled = info !== undefined && !info.allow_ai_assistants;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SmartToyIcon />
        AI assistants
      </DialogTitle>
      <DialogContent>
        {disabled ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your organisation has disabled AI assistant connections. Contact
            your administrator to enable them.
          </Alert>
        ) : mcpUrl ? (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Connect Claude to Mitigate
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              In Claude, go to <strong>Settings → Connectors → Add custom
              connector</strong> and paste this URL:
            </Typography>
            <CopyField value={mcpUrl} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1.5 }}>
              Claude will open Mitigate in your browser — sign in, choose what
              the assistant is allowed to do, and you're connected. Using
              Claude Code? Run this instead, then <code>/mcp</code> to
              authenticate:
            </Typography>
            <CopyField value={`claude mcp add --transport http mitigate ${mcpUrl}`} />
            <Divider sx={{ my: 2.5 }} />
          </>
        ) : null}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Connected assistants
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These assistants can access Mitigate as you, limited to the access
          you granted when connecting. Disconnecting takes effect within a
          minute.
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : connections.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No assistants connected yet.
          </Typography>
        ) : (
          <List dense>
            {connections.map((conn) => (
              <ListItem
                key={conn.id}
                secondaryAction={
                  <Tooltip title="Disconnect">
                    <Button
                      size="small"
                      color="error"
                      startIcon={<LinkOffIcon />}
                      disabled={revokingId === conn.id}
                      onClick={() => handleRevoke(conn)}
                    >
                      Disconnect
                    </Button>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {conn.client_name}
                      {conn.scopes.map((scope) => (
                        <Chip key={scope} label={scope} size="small" variant="outlined" />
                      ))}
                    </Box>
                  }
                  secondary={`Connected ${new Date(conn.created_at).toLocaleDateString()} · ${formatDate(conn.last_used_at)}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
